import "./TaskGroup.scss";
import { useState, useEffect, useRef } from "react";
import Task from "./Task";
import Spinner from "../../../components/Spinner/Spinner";
import { fetcher } from "../../../utils/fetcher";
import { ChevronRight, ChevronDown, Trash01 } from "@untitled-ui/icons-react";
import { useDroppable } from "@dnd-kit/core";

import Modal from "../../../components/Modal/Modal";
import RemoveTaskGroup from "../Modals/RemoveTaskGroup/RemoveTaskGroup";
import EditTaskGroup from "../Modals/EditTaskGroup/EditTaskGroup";

function TaskGroup({
  name,
  data,
  fetchTasks,
  collapsed,
  onToggle,
  showRightSideBar,
  hideRightSideBar,
  taskGroups,
  showCompleted,
  selectedTaskId,
  sortColumn,
  sortDirection,
  onSort,
  onConfigureStatus,
  onConfigurePriority,
  statusConfig,
  priorityConfig,
}) {
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const inputRef = useRef(null);
  const [isSpinnerVisible, setSpinnerVisible] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isCollapsedHeaderHovered, setIsCollapsedHeaderHovered] =
    useState(false);
  const [statusHeaderHovered, setStatusHeaderHovered] = useState(false);
  const [priorityHeaderHovered, setPriorityHeaderHovered] = useState(false);
  const modalRef = useRef();

  // Create a droppable zone for empty sections
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-${data._id}`,
    data: {
      type: "taskgroup",
      sectionId: data._id,
    },
  });

  const showSpinner = () => setSpinnerVisible(true);
  const hideSpinner = () => setSpinnerVisible(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setNewTaskTitle("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openModal = (e, arrow, content) => {
    e.stopPropagation();
    const triggerElement = e.currentTarget;
    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const createTask = async (groupId, taskData) => {
    try {
      // showSpinner();
      const response = await fetcher.post("/api/tasks", {
        groupId,
        ...taskData,
      });
      // hideSpinner();
      return response;
    } catch (err) {
      // hideSpinner();
      console.error("Error creating task:", err);
      throw err;
    }
  };

  const handleAddTask = async (e) => {
    if (e.key === "Enter" && newTaskTitle.trim() !== "") {
      const taskData = {
        groupId: data.id,
        title: newTaskTitle.trim(),
        responsible: "",
        status: "Not Started",
        description: "",
        isActive: true,
      };

      try {
        await createTask(data.id, taskData);
        setNewTaskTitle("");
        if (fetchTasks) fetchTasks();
      } catch (err) {
        console.error("Error creating task:", err);
        console.error("Full error details:", err.response || err);
      }
    } else if (e.key === "Escape") {
      setNewTaskTitle("");
    }
  };

  const cancelRemoveTaskGroup = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const cancelEditTaskGroup = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const updateTaskGroup = async (id, name, color) => {
    try {
      // showSpinner();
      await fetcher.put(`/api/sections/${id}`, { name, color });
      if (fetchTasks) fetchTasks();
      if (modalRef.current) modalRef.current.close();
    } catch (err) {
      console.error("Error updating section:", err);
    } finally {
      // hideSpinner();
    }
  };

  const removeTaskGroup = async (id) => {
    try {
      // showSpinner();
      await fetcher.put(`/api/sections/${id}`, { isActive: false });
      if (fetchTasks) fetchTasks();
    } catch (err) {
      console.error("Error removing section:", err);
    } finally {
      // hideSpinner();
      if (modalRef.current) modalRef.current.close();
    }
  };

  return (
    <div
      className="task-group-wrapper"
      style={{ "--task-group-color": data.color }}
    >
      <Modal ref={modalRef}></Modal>
      {!collapsed ? (
        <>
          <div
            className="task-group-header"
            onMouseEnter={() => setIsHeaderHovered(true)}
            onMouseLeave={() => setIsHeaderHovered(false)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  marginRight: 0,
                  marginLeft: "8px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  onClick={onToggle}
                  style={{
                    fontSize: "28px",
                    color: data.color,
                    cursor: "pointer",
                  }}
                >
                  keyboard_arrow_down
                </span>
              </span>
              <span className="task-group-name">{name}</span>
              <span
                className="material-symbols-outlined more-horiz"
                onClick={(e) =>
                  openModal(
                    e,
                    "auto-top-left",
                    <EditTaskGroup
                      taskGroupData={data}
                      updateTaskGroup={updateTaskGroup}
                      cancelEditTaskGroup={cancelEditTaskGroup}
                    />,
                  )
                }
                style={{
                  fontSize: "24px",
                  marginBottom: "6px",
                  marginLeft: "10px",
                  opacity: isHeaderHovered ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  cursor: "pointer",
                }}
              >
                more_horiz
              </span>
            </div>
            <div
              className="delete-taskgroup-icon"
              style={{ cursor: "pointer", marginRight: "20px" }}
              onClick={(e) =>
                openModal(
                  e,
                  "center-center",
                  <RemoveTaskGroup
                    removeTaskGroup={() => removeTaskGroup(data._id)}
                    cancelRemoveTaskGroup={cancelRemoveTaskGroup}
                  />,
                )
              }
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "22px" }}
              >
                delete
              </span>
            </div>
          </div>
          <div className="task-group">
            <div className="header-cell">
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "16px" }}
              >
                check
              </span>
            </div>
            <div className="header-cell">
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "16px" }}
              >
                format_list_bulleted_add
              </span>
            </div>
            <div
              className="header-cell sortable"
              onClick={() => onSort && onSort("title")}
              style={{ cursor: onSort ? "pointer" : "default" }}
            >
              Title
              {sortColumn === "title" && (
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px", marginLeft: "4px" }}
                >
                  {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
                </span>
              )}
            </div>
            <div
              className="header-cell sortable config-header"
              onMouseEnter={() => setStatusHeaderHovered(true)}
              onMouseLeave={() => setStatusHeaderHovered(false)}
              style={{
                cursor: onSort ? "pointer" : "default",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                className={`config-header-content ${statusHeaderHovered ? "hovered" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: statusHeaderHovered ? "flex-start" : "center",
                  transition: "justify-content 0.3s ease",
                  width: "100%",
                }}
                onClick={() => onSort && onSort("status")}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  Status
                  {sortColumn === "status" && (
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px", marginLeft: "4px" }}
                    >
                      {sortDirection === "asc"
                        ? "arrow_upward"
                        : "arrow_downward"}
                    </span>
                  )}
                </span>
              </div>
              <span
                className="material-symbols-outlined header-config-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigureStatus();
                }}
                style={{
                  fontSize: "16px",
                  cursor: "pointer",
                  position: "absolute",
                  right: "8px",
                  opacity: statusHeaderHovered ? 0.7 : 0,
                  transition: "opacity 0.3s ease",
                  pointerEvents: statusHeaderHovered ? "auto" : "none",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                more_horiz
              </span>
            </div>
            <div
              className="header-cell sortable"
              onClick={() => onSort && onSort("responsible")}
              style={{ cursor: onSort ? "pointer" : "default" }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                person
              </span>
              {sortColumn === "responsible" && (
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px", marginLeft: "4px" }}
                >
                  {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
                </span>
              )}
            </div>
            <div className="header-cell">
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                link
              </span>
            </div>
            <div className="header-cell">
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                image
              </span>
            </div>
            <div
              className="header-cell sortable"
              onClick={() => onSort && onSort("deadline")}
              style={{ cursor: onSort ? "pointer" : "default" }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                Alarm
              </span>
              {sortColumn === "deadline" && (
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px", marginLeft: "4px" }}
                >
                  {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
                </span>
              )}
            </div>
            <div
              className="header-cell sortable config-header"
              onMouseEnter={() => setPriorityHeaderHovered(true)}
              onMouseLeave={() => setPriorityHeaderHovered(false)}
              style={{
                cursor: onSort ? "pointer" : "default",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                className={`config-header-content ${priorityHeaderHovered ? "hovered" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: priorityHeaderHovered
                    ? "flex-start"
                    : "center",
                  transition: "justify-content 0.3s ease",
                  width: "100%",
                }}
                onClick={() => onSort && onSort("priority")}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  Priority
                  {sortColumn === "priority" && (
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px", marginLeft: "4px" }}
                    >
                      {sortDirection === "asc"
                        ? "arrow_upward"
                        : "arrow_downward"}
                    </span>
                  )}
                </span>
              </div>
              <span
                className="material-symbols-outlined header-config-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigurePriority();
                }}
                style={{
                  fontSize: "16px",
                  cursor: "pointer",
                  position: "absolute",
                  right: "8px",
                  opacity: priorityHeaderHovered ? 0.7 : 0,
                  transition: "opacity 0.3s ease",
                  pointerEvents: priorityHeaderHovered ? "auto" : "none",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                more_horiz
              </span>
            </div>
            <div className="header-cell">Description</div>
            <div className="header-cell">&nbsp;</div>
            <div className="header-cell">&nbsp;</div>
            <div className="header-cell">&nbsp;</div>
            {data.tasks.length === 0 ? (
              // Empty dropzone when no tasks
              <div
                ref={setNodeRef}
                className={`empty-task-dropzone ${isOver ? "over" : ""}`}
                style={{
                  gridColumn: "1 / -1",
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--main-text-color)",
                  opacity: 0.5,
                  fontSize: "12px",
                  borderBottom: "1px solid var(--task-line-color)",
                  backgroundColor: "var(--task-background-color)",
                  minHeight: "20px",
                }}
              >
                {isOver ? "No tasks" : "No tasks"}
              </div>
            ) : (
              // Render tasks normally - only show main tasks (not subtasks)
              data.tasks
                .filter((task) => !task.isSubtask && !task.parentTaskId)
                .map((task) => (
                  <Task
                    key={task._id}
                    data={task}
                    fetchTasks={fetchTasks}
                    showRightSideBar={showRightSideBar}
                    hideRightSideBar={hideRightSideBar}
                    taskGroups={taskGroups}
                    showCompleted={showCompleted}
                    subtasks={data.tasks.filter(
                      (t) => t.parentTaskId === task._id && t.isSubtask,
                    )}
                    selectedTaskId={selectedTaskId}
                    statusConfig={statusConfig}
                    priorityConfig={priorityConfig}
                  />
                ))
            )}
            <div></div>
            <div className="new-task" style={{ gridColumn: "2 / -1" }}>
              <input
                type="text"
                className="new-task"
                ref={inputRef}
                placeholder="+ Add Task"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={handleAddTask}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="task-group collapsed">
            <div className="header-cell">
              {" "}
              <div
                className="task-group-header"
                onMouseEnter={() => setIsCollapsedHeaderHovered(true)}
                onMouseLeave={() => setIsCollapsedHeaderHovered(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      marginRight: 0,
                      marginLeft: "8px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span
                      className="material-symbols-outlined"
                      onClick={onToggle}
                      style={{
                        fontSize: "28px",
                        color: data.color,
                        cursor: "pointer",
                        // marginLeft: "10px",
                      }}
                    >
                      chevron_right
                    </span>
                  </span>
                  <span className="task-group-name">{name}</span>
                  <span className="task-group-count">
                    ({data.tasks.length}{" "}
                    {data.tasks.length === 1 ? "task" : "tasks"})
                  </span>
                  <span
                    className="material-symbols-outlined more-horiz"
                    onClick={(e) => {
                      openModal(
                        e,
                        "auto-top-left",
                        <EditTaskGroup
                          taskGroupData={data}
                          updateTaskGroup={updateTaskGroup}
                          cancelEditTaskGroup={cancelEditTaskGroup}
                        />,
                      );
                    }}
                    style={{
                      fontSize: "24px",
                      marginBottom: "6px",
                      marginLeft: "10px",
                      opacity: isCollapsedHeaderHovered ? 1 : 0,
                      transition: "opacity 0.2s ease",
                      cursor: "pointer",
                    }}
                  >
                    more_horiz
                  </span>
                </div>
                <div
                  className="delete-taskgroup-icon"
                  style={{ cursor: "pointer", marginRight: "20px" }}
                  onClick={(e) =>
                    e.stopPropagation() ||
                    openModal(
                      e,
                      "center-center",
                      <RemoveTaskGroup
                        removeTaskGroup={() => removeTaskGroup(data._id)}
                        cancelRemoveTaskGroup={cancelRemoveTaskGroup}
                      />,
                    )
                  }
                >
                  <Trash01 size={24} />
                </div>
              </div>
            </div>
            <div></div>
          </div>
        </>
      )}
      {isSpinnerVisible && <Spinner />}
    </div>
  );
}

export default TaskGroup;
